import { computed, action, observable, makeObservable } from 'mobx';
import { slug } from 'src/utils/deburr';
import { LocalStorageItemState } from './LocalStorageItemState';
import { TranslationsStore } from 'src/domains/layouts/state/translationsStore/TranslationsStore';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { EventsCollectionState } from 'src/domains/sportsbook/shared/Types';
import { RouteViewType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { Common } from 'src/domains/common/Common';
import { LocalStorageState } from './LocalStorageState';
import { AppCasinoState } from 'src/domains/casino/state/AppCasino.state';

interface RecentlyVisitedItemType {
    key: string;
    label: string;
    to: RouteViewType;
}

export type RecentlyVisitedLocalStorageObjectType = {
    type: 'event' | 'sport' | 'casino';
    id: string;
};

export class RecentlyVisitedLocalStorage {
    private readonly translationsStore: TranslationsStore;
    private readonly configComponents: ConfigComponents;

    @observable public recentlyVisited: LocalStorageItemState<Array<RecentlyVisitedLocalStorageObjectType>>;

    public constructor(private readonly common: Common) {
        makeObservable(this);
        const localStorageState = LocalStorageState.get(common);

        this.recentlyVisited = localStorageState.cgRecentlyVisited;
        this.translationsStore = TranslationsStore.get(common);
        this.configComponents = ConfigComponents.get(common);
    }

    private pushNewItem(type: 'event' | 'sport' | 'casino', id: string): void {
        const currentList = this.recentlyVisited.getValue();
        const currentListFiltered = currentList.filter((value) => {
            const isEq = value.type === type && value.id === id;
            return !isEq;
        });

        currentListFiltered.push({
            type,
            id,
        });

        this.recentlyVisited.setValue(currentListFiltered);
    }

    @action public setItem = (route: RouteViewType): void => {
        if (route.name === 'sport') {
            this.pushNewItem('sport', route.id);
        }

        if (route.name === 'casino') {
            //TODO - This part didn't work. To be verified with PO casino what about this code
            // if (route.tabId !== null) {
            //     this.pushNewItem('casino', route.tabId);
            // }
        }

        if (route.name === 'event') {
            this.pushNewItem('event', route.id.toString());
        }

        // filter localStorage and remove no active events
        this.recentlyVisited.setValue(
            this.recentlyVisited.getValue().filter((value) => {
                if (value.type === 'event') {
                    const eventIdNumber = parseInt(value.id, 10);
                    const eventId = this.common.models.id.getEventId(eventIdNumber);

                    const event = eventId.getEventModel();
                    if (event !== null && event.active && event.display) {
                        return true;
                    } else {
                        return false;
                    }
                }

                if (route.name === 'sport' || route.name === 'casino') {
                    return true;
                }

                return true;
            })
        );

        if (this.recentlyVisited.getValue().length > 10) {
            this.recentlyVisited.getValue().splice(0, this.recentlyVisited.getValue().length - 10);
        }
    };

    private linkParams({ type, id }: RecentlyVisitedLocalStorageObjectType): RecentlyVisitedItemType | null {
        switch (type) {
            case 'sport': {
                const sportModel = this.common.models.getSport(id);
                const eventsCollection = EventsCollectionState.get(this.common);
                const eventsInSport = eventsCollection.listOfSport(id).ids.length;

                if (sportModel !== null || eventsInSport !== 0) {
                    return {
                        key: `recently-sport-${id}`,
                        label: this.translationsStore.translateSport(id),
                        to: {
                            name: 'sport',
                            nameType: 'regular',
                            id,
                        },
                    };
                }
                return null;
            }
            case 'casino': {
                const appCasinoState = AppCasinoState.get(this.common);
                const gameId = parseInt(id);
                const game = appCasinoState.gameStoreExt.getGameModel(gameId)?.GameSharedType ?? null;

                if (game !== null) {
                    return {
                        key: `recently-casino-${id}`,
                        label: game.name,
                        to: {
                            name: 'casino',
                            tabId: id,
                        },
                    };
                }
                return null;
            }
            case 'event': {
                const eventIdNumber = parseInt(id, 10);

                if (isNaN(eventIdNumber)) {
                    return null;
                }

                const eventId = this.common.models.id.getEventId(eventIdNumber);

                const event = eventId.getEventModel();

                if (event !== null && event.active && event.display) {
                    return {
                        key: `recently-event-${id}`,
                        label: event.name,
                        to: {
                            name: 'event',
                            id: event.id,
                            slug: slug(event.name),
                        },
                    };
                }

                return null;
            }
            default:
                return null;
        }
    }

    @computed.struct public get mapRecentlyVisitedToLinks(): Array<RecentlyVisitedItemType> {
        const out: Array<RecentlyVisitedItemType> = [];
        const { hiddenSportsList } = this.configComponents.config;

        for (const recentlyVisited of this.recentlyVisited.getValue()) {
            if (recentlyVisited.type === 'sport' && hiddenSportsList.includes(recentlyVisited.id)) {
                continue;
            }

            const link = this.linkParams(recentlyVisited);
            const sportsAndEventsListAllowed = EventsCollectionState.get(this.common).sportsAndEventsListV2;

            if (link !== null && sportsAndEventsListAllowed.has(recentlyVisited.id)) {
                out.push(link);
            }
        }
        return out.reverse();
    }
}
